import { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { Eventcalendar, getJson, formatDate, localeDe, Draggable, Dropcontainer } from '@mobiscroll/react';
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession  } from "aws-amplify/auth";
import { UserContext } from "../../App";
import { isHDLectureManager, hasHDIndustry } from "../../config/hdLectureManager";
import { MDBBtn, MDBCard, MDBCardBody, MDBCardTitle, MDBCheckbox, MDBChip, MDBCol, MDBDatepicker, MDBIcon, MDBInput, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalHeader, MDBModalTitle, MDBRow, MDBSelect, MDBTable, MDBTableBody, MDBTabs, MDBTabsItem, MDBTypography } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import moment from 'moment-timezone';

import getEventDays from '../../utils/getEventDays';
import { getEventDayHours } from '../../utils/getEventDayHours';
import usePrevious from "../../utils/usePrevious";
import { getLocalizedText } from "../../utils/localizedText";

import LectureModal from './scheduling/LectureModal';
import LectureForm from "./LectureForm";
import { isoToGermanDate } from "../../utils/dateTools";
import { captureError } from "../../utils/sentryHelpers";


const client = generateClient();



export default function Scheduling() {

    const { t, i18n } = useTranslation();
    const { user } = useContext(UserContext);
    const restrictedHD = isHDLectureManager(user);

    const config = {
        openTime: "10:00",
        closeTime: "18:00",
        rooms: 10
    }


    // States
    const [isLoading, setIsLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [event, setEvent] = useState({});
    const [eventDays, setEventDays] = useState([]); // Event days
    const [rooms, setRooms] = useState([]); // Rooms
    const [lectures, setLectures] = useState([]); // Lectures
    const [shownLectures, setShownLectures] = useState([]);
    const [lecturesCalendar, setLecturesCalendar] = useState([]);
    const [totalLectures, setTotalLectures] = useState(0);
    const [topics, setTopics] = useState([]);
    const [topicGroups, setTopicGroups] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedEventId, setSelectedEventId] = useState(searchParams.get('eventId'));
    const [filtersInitialized, setFiltersInitialized] = useState(false);
    const [filters, setFilters] = useState({
      approved: null,
      createdAtStart: "2023-12-01T00:00:00.000Z",
      createdAtEnd: null,
      createdAtSort: "desc",
      status: null,
      type: null
    });
    
    const [filterTopics, setFilterTopics] = useState([]);

    const [useFilterInCalendar, setUseFilterInCalendar] = useState(false);


    const getEvents = async () => {
      try {
          const events = await client.graphql({
              query: /* GraphQL */ `
              query ListEvents {
                  listEvents {
                      items {
                          id
                          name {
                              text
                              language
                          }
                          startDate
                          endDate
                          startTime
                          endTime
                          status
                          industries {
                              items {
                                  industry {
                                      id
                                  }
                              }
                          }
                      }
                  }
              }
              `,
              // variables: {
              //     filter: {
              //         startDate: {
              //             ge: new Date().toISOString().substring(0, 10)
              //         }
              //     }
              // }
          });
          let items = events.data.listEvents.items.filter(event => event.status === "active");
          if (restrictedHD) {
              items = items.filter(hasHDIndustry);
          }
          setEvents(items);
          return events.data.listEvents.items;
      } catch (error) {
          captureError(error, {
              feature: 'lecture-management',
              operation: 'fetch-events',
              tags: { component: 'Scheduling' }
          });
      }
  }
    // Get Event
    const getEvent = async () => {
        try {
            setIsLoading(true);
            const eventData = await client.graphql({
                query: /* GraphQL */ `
                query GetEvent($id: ID!) {
                  getEvent(id: $id) {
                    id
                    name {
                      language
                      text
                      __typename
                    }
                    startTime
                    endTime
                    daySchedules {
                      day
                      date
                      exhibitorStart
                      exhibitorEnd
                      visitorStart
                      visitorEnd
                    }
                    lectureRooms
                    status
                    image {
                      fileName
                      alt
                      x4UUID
                      __typename
                    }
                    date
                    startDate
                    endDate
                    location {
                      __typename
                    }
                    attendees {
                      nextToken
                      __typename
                    }
                    availableBundleBooths {
                      nextToken
                      __typename
                    }  
                    
                    createdAt
                    updatedAt
                    __typename
                  }
                }
              `,
                variables: {
                    id: selectedEventId
                }
            });
            setEvent(eventData.data.getEvent);
            setEventDays(getEventDays(eventData.data.getEvent));
            // Generate rooms array. Number of rooms is defined in the event data (eventData.data.getEvent.lectureRooms = Int). content for each room is {id: index + 1, name: "Room" + index + 1, color: random color}
            const newRooms = Array.from({length: eventData.data.getEvent.lectureRooms}, (_, index) => ({id: index + 1, name: "Room " + (index + 1), color: `#${Math.floor(Math.random()*16777215).toString(16)}`}));
            setRooms(newRooms);
            console.log("eventdays", getEventDays(eventData.data.getEvent))
            console.log("rooms", newRooms);


        } catch (error) {
            captureError(error, {
                feature: 'lecture-management',
                operation: 'fetch-lectures-and-rooms',
                context: { eventId: selectedEventId },
                tags: { component: 'Scheduling' }
            });
        }
    };



// Handler für Event-Auswahl
const handleEventSelection = (eventId) => {
    setSelectedEventId(eventId);
    setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (eventId) {
            newParams.set('eventId', eventId);
        } else {
            newParams.delete('eventId');
        }
        return newParams;
    });
};

// Angepasste useEffect-Hooks
useEffect(() => {
    // Beim ersten Render die Filter aus den searchParams extrahieren und setzen
    const extractedParams = extractExistingParams(searchParams);
    const eventIdFromParams = searchParams.get('eventId');
    
    setFilters(oldFilters => ({
        ...extractedParams,
        createdAtStart: extractedParams.createdAtStart || oldFilters.createdAtStart,
        createdAtSort: extractedParams.createdAtSort || oldFilters.createdAtSort
    }));

    if (eventIdFromParams) {
        setSelectedEventId(eventIdFromParams);
    }
    
    setFiltersInitialized(true);
}, []); // Leere Abhängigkeiten, um den Hook nur einmal auszuführen

// Angepasster useEffect für das Laden der Events
useEffect(() => {
    getEvents().then(() => {
        // Nach dem Laden der Events, wenn eine eventId in der URL ist,
        // können wir sicher sein, dass die Event-Daten verfügbar sind
        const eventIdFromParams = searchParams.get('eventId');
        if (eventIdFromParams) {
            setSelectedEventId(eventIdFromParams);
        }
    });
}, []);

// Angepasster useEffect für Filter-Updates
useEffect(() => {
    if (filtersInitialized) {
        const params = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value !== null) {
                acc[key] = value;
            }
            return acc;
        }, {});

        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            // Bestehende eventId beibehalten
            const currentEventId = prev.get('eventId');
            if (currentEventId) {
                newParams.set('eventId', currentEventId);
            }
            
            // Filter-Parameter setzen
            Object.entries(params).forEach(([key, value]) => {
                newParams.set(key, value);
            });
            
            return newParams;
        });

        if (selectedEventId) {
            getLectures();
        }
    }
}, [filters, filtersInitialized]);

// Angepasster useEffect für Event-Änderungen
useEffect(() => {
    if (selectedEventId) {
        getEvent();
        getLectures();
    }
}, [selectedEventId]);
    

     // Topics data
  // Specific topic group IDs to filter for LOUNGES events
  const ALLOWED_TOPIC_GROUP_IDS = [
    'b1f2ca84-0f9a-4d08-9b26-fd652a3ac5fc',
    '93ffdc86-e278-4a68-88b8-2d81a28cde65'
  ];

  const getTopics = async () => {
    try {
      const topicGroupsWithTopics = await client.graphql({
        query: /* GraphQL */ `
        query ListTopicGroups(
          $filter: ModelTopicGroupFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listTopicGroups(filter: $filter, limit: $limit, nextToken: $nextToken) {
            items {
              id
              color
              sortKey
              name {
                language
                text
              }
              topics {
                items {
                  id
                  name {
                    language
                    text
                  }
                  color
                  sortKey
                  topicGroup {
                    color
                  }
                  topicGroupTopicsId
                }
              }
            }
            nextToken
            __typename
          }
        }
      `
        });

        // Filter only the allowed topic groups
        const filteredTopicGroups = topicGroupsWithTopics.data.listTopicGroups.items
          .filter(group => ALLOWED_TOPIC_GROUP_IDS.includes(group.id))
          .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));

        setTopicGroups(filteredTopicGroups);

        // Flatten topics from filtered groups
        const topicsFlat = filteredTopicGroups
          .map((topicGroup) => topicGroup.topics.items)
          .flat()
          .sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0));

        setTopics(topicsFlat);

    } catch (error) {
        console.log("error on fetching topics", error);
    }
}

useEffect(() => {
    if (topics.length === 0) {
        getTopics();
    }
}, [topics]);
    
    const extractExistingParams = (searchParams) => {
        return Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
            if (value === 'true') {
                acc[key] = true;
            } else if (value === 'false') {
                acc[key] = false;
            } else if (key === 'searchTerm') {
              setSearchTerm(value);
            } else {
                acc[key] = value;
            }
            return acc;
        }, {});
    }

    const getLectures = async () => {
        try {
            setIsLoading(true);
            const searchLectureFilter = {
            eventLecturesId: { eq: selectedEventId }
            };
            const searchLectureSort = {};
            if (typeof filters.approved === "boolean") {
                searchLectureFilter.approved = { eq: filters.approved };
            }
            if (filters.createdAtStart && filters.createdAtEnd) {
                searchLectureFilter.and = [{createdAt: {gt: filters.createdAtStart}}, {createdAt: {lt: filters.createdAtEnd}}];
            } else if (filters.createdAtStart) {
                searchLectureFilter.createdAt = { gt: filters.createdAtStart };
            } else if (filters.createdAtEnd) {
                searchLectureFilter.createdAt = { lt: filters.createdAtEnd };
            }
            if (filters.createdAtSort) {
                searchLectureSort.field = "createdAt";
                searchLectureSort.direction = filters.createdAtSort;
            }
            if (filters.status) {
                searchLectureFilter.status = { eq: filters.status };
            }
            if (filters.type) {
                searchLectureFilter.type = { eq: filters.type };
            } 
            else {
                searchLectureFilter.dateTimes = { exists: false };
            }

            const lectures = await client.graphql({
                variables: {
                    filter: searchLectureFilter,
                    sort: searchLectureSort,
                    limit: 1000
                },
                query: /* GraphQL */ `
                query SearchLectures(
                  $filter: SearchableLectureFilterInput
                  $sort: [SearchableLectureSortInput]
                  $limit: Int
                  $nextToken: String
                  $from: Int
                  $aggregates: [SearchableLectureAggregationInput]
                ) {
                  searchLectures(
                    filter: $filter
                    sort: $sort
                    limit: $limit
                    nextToken: $nextToken
                    from: $from
                    aggregates: $aggregates
                  ) {
                    items {
                      lengthInSeconds
                        title {
                            language
                            text
                        }
                        speakers {
                            items {
                                employee {
                                    image {
                                    fileName
                                    alt
                                    }
                                }
                            }
                        }
                        mainSpeakerUser {
                          image {
                            fileName
                            alt
                            }
                            firstName
                            lastName
                        }
                        subjects {
                            id
                            name {
                                language
                                text
                            }
                        } 
                        topics {
                            items {
                              topic {
                                id
                                topicGroupTopicsId
                                name {
                                  language
                                  text
                                }
                              }
                            }
                        }
                        adminTopic {
                          id
                        }    
                        content {
                            language
                            text
                        }
                        conditions {
                            name
                            accepted
                        }
                        image {
                            fileName
                            alt
                        }
                        pdfDe {
                            url
                            name   
                        }
                        pdfEn {
                            url
                            name   
                        }
                        company {
                            id
                            name
                            image {
                                main {
                                    fileName
                                    alt
                                }
                            }
                        }
                        conditions {
                            name
                            accepted
                        }
                        id
                        session
                        startTime
                        endTime
                        room
                        type
                        presentationLanguage
                        createdAt
                        updatedAt
                        companyLecturesId
                        eventLecturesId
                        lectureMainSpeakerId
                        approved
                        status
                        __typename
                    }
                    nextToken
                    total
                    aggregateItems {
                      name
                      result {
                        ... on SearchableAggregateScalarResult {
                          value
                        }
                        ... on SearchableAggregateBucketResult {
                          buckets {
                            key
                            doc_count
                            __typename
                          }
                        }
                      }
                      __typename
                    }
                    __typename
                  }
                }
              `
            });
            setLectures(lectures.data.searchLectures.items.map(lecture => ({...lecture, topics: lecture.topics.items.map(topic => topic.topic)})).sort((a, b) => {if (a.company?.name < b.company?.name) return -1;if (a.company?.name > b.company?.name) return 1;return a.createdAt < b.createdAt ? -1 : 1;}))
            setTotalLectures(lectures.data.searchLectures.total);
            if(searchTerm.length > 0) {
              console.log("searchTerm", searchTerm);
              setShownLectures(lectures.data.searchLectures.items
                .map(lecture => ({...lecture, topics: lecture.topics.items.map(topic => topic.topic)}))
                .sort((a, b) => {if (a.company?.name < b.company?.name) return -1;if (a.company?.name > b.company?.name) return 1;return a.createdAt < b.createdAt ? -1 : 1;})
                .filter(lecture => {
                  if (lecture.company?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())) {
                      return true;
                  }
                  if (lecture.title?.find(t => t.language === i18n.language)?.text?.toLowerCase()?.includes(searchTerm.toLowerCase())) {
                      return true;
                  }
                  return false;
              }).filter(lecture => !lecture.startTime)
            );
            } else {
              setShownLectures(lectures.data.searchLectures.items
                .map(lecture => ({...lecture, topics: lecture.topics.items.map(topic => topic.topic)}))
                .filter(lecture => !lecture.startTime)
                .sort((a, b) => {if (a.company?.name < b.company?.name) return -1;if (a.company?.name > b.company?.name) return 1;return a.createdAt < b.createdAt ? -1 : 1;}))
            }
            setIsLoading(false);
        } catch (error) {
            console.log("error on fetching lectures", error);
            if (error.data?.searchLectures?.items) {
                setLectures(error.data.searchLectures.items);
                setShownLectures(error.data.searchLectures.items);
            }
            setIsLoading(false);
        }
    };

    

    // useEffect(() => {
    //     // Beim ersten Render die Filter aus den searchParams extrahieren und setzen
    //     const extractedParams = extractExistingParams(searchParams);
    //     setFilters(oldFilters => ({ ...extractedParams, createdAtStart: extractedParams.createdAtStart || oldFilters.createdAtStart , createdAtSort: extractedParams.createdAtSort || oldFilters.createdAtSort }));
    //     setFiltersInitialized(true); // Setze auf true, sobald die Initialisierung abgeschlossen ist
    // }, []); // Leere Abhängigkeiten, um den Hook nur einmal auszuführen

    // useEffect(() => {
    //     if (filtersInitialized && selectedEventId) {
    //         // Aktualisiere die searchParams und rufe getLectures auf, nachdem die Filter initialisiert wurden
    //         const params = Object.entries(filters).reduce((acc, [key, value]) => {
    //             if (value !== null) {
    //                 acc[key] = value;
    //             }
    //             return acc;
    //         }, {});

    //         setSearchParams(params);
    //         getLectures();
    //     }
    // }, [filters, filtersInitialized, selectedEventId]); // Abhängigkeit von den Filters und filtersInitialized

    
    const searchLectures = (searchTerm) => {
      setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          if (searchTerm) {
              newParams.set('searchTerm', searchTerm);
          } else {
              newParams.delete('searchTerm');
          }
          return newParams;
      });
      setSearchTerm(searchTerm);
      if (searchTerm.length > 0) {
          setShownLectures(lectures.filter(lecture => {
              if (lecture.company?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())) {
                  return true;
              }
              if (lecture.title?.find(t => t.language === i18n.language)?.text?.toLowerCase()?.includes(searchTerm.toLowerCase())) {
                  return true;
              }
              return false;
          }).filter(lecture => !lecture.startTime));
          if(useFilterInCalendar) {
              setLecturesCalendar(lectures.filter(lecture => {
                  if (lecture.company?.name?.toLowerCase()?.includes(searchTerm.toLowerCase())) {
                      return true;
                  }
                  if (lecture.title?.find(t => t.language === i18n.language)?.text?.toLowerCase()?.includes(searchTerm.toLowerCase())) {
                      return true;
                  }
                  return false;
              }));
          }
      } else {
          setShownLectures(lectures.filter(lecture => !lecture.startTime));
      }
  }

  const filterByTopics = (topicIds) => {
      if (topicIds && topicIds.length > 0) {
          setShownLectures(lectures.filter(lecture => topicIds.includes(lecture.adminTopic?.id)).filter(lecture => !lecture.startTime));
      } else {
          setShownLectures(lectures.filter(lecture => !lecture.startTime));
      }
  }

  useEffect(() => {
      // Only filter if lectures are loaded
      if (lectures.length > 0) {
          filterByTopics(filterTopics);
      }
  }, [filterTopics, lectures]);

  const [selectedConditions, setSelectedConditions] = useState([]); // selectedConditions = ["ECV", "Podcast"
  const prevConditions = usePrevious(selectedConditions);

  const filterByConditions = async () => { // selectedConditions = ["ECV", "Podcast"]
      console.log(selectedConditions);
     
      if (selectedConditions.length > 0) {
         
          setShownLectures(shownLectures.filter(lecture => {
              if(lecture.conditions?.length > 0) {
              if (selectedConditions.includes("ECV") && selectedConditions.includes("Podcast")) {
                  return (lecture.conditions?.find(condition => condition.name === "ecv")?.accepted && lecture.conditions?.find(condition => condition.name === "podcast")?.accepted);
              } else if (selectedConditions.includes("ECV")) {
                  return lecture.conditions?.find(condition => condition.name === "ecv")?.accepted;
              } else if (selectedConditions.includes("Podcast")) {
                  return lecture.conditions?.find(condition => condition.name === "podcast")?.accepted;
              }
              return false;
          } else {
              return false;
          }
          }).filter(lecture => !lecture.startTime));
      } else {
          setShownLectures(lectures.filter(lecture => !lecture.startTime));
      }
  }

  useEffect(() => {
     // If something changed in the selectedConditions, filter the lectures
       if (prevConditions !== selectedConditions) {
      filterByConditions(selectedConditions);
      }
  }, [selectedConditions, shownLectures]);

    // Calendar components

    // Get topic color with fallback (topic color -> topicGroup color -> light gray for unassigned)
    const getTopicColor = (adminTopicId) => {
      if (!adminTopicId) return "#f5f5f5"; // Light gray for lectures without assigned topic
      const topic = topics.find(t => t.id === adminTopicId);
      return topic?.color || topic?.topicGroup?.color || "#f5f5f5";
    };

    function Task({lecture, id}) {
      const [draggable, setDraggable] = useState();
  const [mouseDownTime, setMouseDownTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  const setDragElm = useCallback((elm) => {
    setDraggable(elm);
  }, []);

  const handleMouseDown = () => {
    setMouseDownTime(Date.now());
    setIsDragging(false);
  };

  const handleMouseMove = () => {
    if (mouseDownTime > 0) {
      setIsDragging(true);
    }
  };

  const handleMouseUp = (e) => {
    const mouseUpTime = Date.now();
    const timeDiff = mouseUpTime - mouseDownTime;
    
    // Wenn die Maus weniger als 200ms gedrückt war und sich nicht bewegt hat,
    // behandeln wir es als Klick
    if (timeDiff < 200 && !isDragging) {
      handleLectureClick(lecture);
    }
    
    setMouseDownTime(0);
    setIsDragging(false);
  };

  let event = {
    id: lecture.id,
    title: lecture.title?.find((title) => title.language === i18n.language)?.text,
    length: "0.5 h",
    start: "10:00",
    end: lecture.lengthInSeconds ? new Date(new Date(`1970-01-01T10:00:00Z`).getTime() + (lecture.lengthInSeconds || 30) * 1000).toISOString().substr(11, 5) : "10:30",
    color: getTopicColor(lecture.adminTopic?.id),
    cssClass: "external-drop-task",
    lecture
  };

  return (
    <div>
      {!event.hide && (
        <div 
          ref={setDragElm} 
          className="external-drop-task mb-2"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            setMouseDownTime(0);
            setIsDragging(false);
          }}
        >
          <MDBCard
            className={"shadow-3" + (lecture.type?.includes("promotion") ? " border border-dark" : "")}
            style={{
              backgroundColor: getTopicColor(lecture.adminTopic?.id),
              cursor: "grab"
            }}
          >
              <MDBCardBody className="p-2">
              <div className='d-flex align-items-center'>
              
                {lecture.company?.image?.main?.fileName ?
                    <div className='d-flex align-items-center'>
                        {lecture.company.image?.main?.fileName ?
                            <img
                                src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/"+lecture.company.image.main.fileName}
                                className="rounded-circle"
                                style={{ height: "50px", width: "50px", objectFit: "contain" }}
                                alt={lecture.company.image.alt}
                            />
                        :
                            <MDBIcon className="rounded-circle" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="company-alt" />
                                    
                        }
                        
                    </div>
                    :
                    <MDBIcon
                        className="rounded-circle"
                        style={{
                            height: "50px",
                            width: "50px",
                            textAlign: "center",
                            paddingTop: "12px",
                            fontSize: "20px",
                        }}
                        fas
                        icon={lecture.type === "organizatorical" ? "clock" : "building"}
                    />
                }
               <div className="ms-2">
               <span className="fw-bold" style={{lineHeight: "1", display: "block", fontSize: "15px"}}>{getLocalizedText(lecture.title).substring(0, 50)}</span>
               <span className="d-block mt-2" style={{lineHeight: "1", fontSize: "10px"}}>{lecture.company ? lecture.company?.name : lecture.mainSpeakerUser ? (lecture.mainSpeakerUser.firstName +  " " + lecture.mainSpeakerUser.lastName) : ""}</span>
              </div>
               </div>
              </MDBCardBody>
            </MDBCard>
            <Draggable dragData={event} element={draggable} />
            </div>
          )}
        </div>
      );
    }

    const customScheduleEvent = useCallback((data) => {
      const lecture = data.original.lecture;
      if(!lecture) return null;
        return (
          <MDBCard className={"shadow-3 h-100"} style={lecture.type === "organizatorical" ? {backgroundColor: "#b5b5b5",color: "#000"} : {backgroundColor: data.color}}>
              <MDBCardBody className="p-0">
              <div className='d-flex align-items-center'>
              
                {lecture.company?.image?.main?.fileName ?
                    <div className='d-flex align-items-center'>
                        {lecture.company.image?.main?.fileName ?
                            <img
                                src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/"+lecture.company.image.main.fileName}
                                className="rounded-circle"
                                style={{ height: "50px", width: "50px", objectFit: "contain" }}
                                alt={lecture.company.image.alt}
                            />
                        :
                            <MDBIcon className="rounded-circle" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="company-alt" />
                                    
                        }
                        
                    </div>
                    :
                    <MDBIcon
                        className="rounded-circle"
                        style={{
                            height: "50px",
                            width: "50px",
                            textAlign: "center",
                            paddingTop: "12px",
                            fontSize: "20px",
                        }}
                        fas
                        icon={lecture.type === "organizatorical" ? "clock" : "building"}
                    />
                }
               <div className="ms-2">
               <span className="fw-bold" style={{lineHeight: "1", display: "block", fontSize: "12px", color: "#2b2b2b"}}>{getLocalizedText(lecture.title)?.substring(0, 50)}</span>
               <span className="d-block mt-1" style={{lineHeight: "1", fontSize: "10px", color: "#2b2b2b"}}>{lecture.company ? lecture.company?.name.substring(0, 50) : lecture.mainSpeakerUser ? (lecture.mainSpeakerUser.firstName +  " " + lecture.mainSpeakerUser.lastName) : ""}</span>
              </div>
               </div>
              </MDBCardBody>
            </MDBCard>
        );
      
    }, []);

  
  const [dropCont, setDropCont] = useState();
  const setDropElm = useCallback((elm) => {
    setDropCont(elm);
  }, []);

  const updateLecture = async (lecture) => {
    try {
      const updatedLecture = await client.graphql({
        query: /* GraphQL */ `
        mutation UpdateLecture(
          $input: UpdateLectureInput!
          $condition: ModelLectureConditionInput
        ) {
          updateLecture(input: $input, condition: $condition) {
            id
          }
        }
      `,
        variables: {
          input: {
            id: lecture.id,
            room: lecture.room,
            startTime: lecture.startTime,
            endTime: lecture.endTime
          }
        }
      });
      // console.log("updatedLecture", updatedLecture);
    } catch (error) {
      console.log("error on updating lecture", error);
    }
  }

  const onEventCreate = useCallback(
    async (args) => {
      console.log("args create", args)
      try {
        const startTime = moment(args.event.start).tz('Europe/Berlin').format();
        const endTime = moment(args.event.end).tz('Europe/Berlin').format();
        const updatedLecture = updateLecture({...args.event.lecture, startTime, endTime, room: args.event.resource});
        // console.log("updatedLecture", updatedLecture);


        setLectures(lectures.map(lecture => {
          if (lecture.id === args.event.id) {
              return {
                ...lecture,
                startTime: args.event.start.toISOString(),
                endTime: args.event.end.toISOString(),
                room: args.event.resource
              };
          }
          return lecture;
        }));

        setShownLectures(oldShownLectures => oldShownLectures.filter(lecture => lecture.id !== args.event.id && lecture !== null && !lecture.startTime).sort((a, b) => {if (a.company?.name < b.company?.name) return -1;if (a.company?.name > b.company?.name) return 1;return a.createdAt < b.createdAt ? -1 : 1;}));
        
        setLecturesCalendar(oldLecturesCalendar => [...oldLecturesCalendar, {...args.event.lecture, startTime, endTime, room: args.event.resource}] );
      } catch (error) {
        console.log("error on updating lecture", error);
      }
    },
    [lectures],
  );

  const onEventDelete = useCallback(async (args) => {
    console.log("args delete", args)
    try {
      const updatedLecture = updateLecture({...args.event.lecture, startTime: null, endTime: null, room: null});
      console.log("updatedLecture", updatedLecture);
      setLectures(oldLectures => oldLectures.map(lecture => {
        if (lecture.id === args.event.id) {
            return {
                ...lecture,
                startTime: null,
                endTime: null,
                room: null
            };
        }
        return lecture;
      }));
    
      setShownLectures(oldShownLectures => [...oldShownLectures, {...args.event.lecture, startTime: null, endTime: null, room: null}].sort((a, b) => {if (a.company?.name < b.company?.name) return -1;if (a.company?.name > b.company?.name) return 1;return a.createdAt < b.createdAt ? -1 : 1;}));
    
      setLecturesCalendar(oldLecturesCalendar => oldLecturesCalendar.filter(lecture => lecture.id !== args.event.lecture.id));
      
    } catch (error) {
      console.log("error on updating lecture", error);
    }
   
  }, []);

  const onEventUpdate = useCallback((args) => {
    console.log("args update", args)
    try {
      const startTime = moment(args.event.start).tz('Europe/Berlin').format();
      const endTime = moment(args.event.end).tz('Europe/Berlin').format();
      const updatedLecture = updateLecture({...args.event.lecture, startTime, endTime, room: args.event.resource});
      setLectures(oldLectures => oldLectures.map(lecture => {
        if (lecture.id === args.event.id) {
            return {
                ...lecture,
                startTime,
                endTime,
                room: args.event.resource
            };
        }
        return lecture;
    }));
      console.log("updatedLecture", updatedLecture);
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'update-lecture-room',
        context: { lectureId: args.event.lecture.id, newRoom: args.resource },
        tags: { component: 'Scheduling' }
      });
    }
  }, []);


  const onItemDrop = (args) => {
    console.log("args drop", args)
  }

  // Lecture View
  const [selectedLecture, setSelectedLecture] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleLectureClick = (lecture) => {
    setSelectedLecture(lecture);
    setIsModalOpen(true);
  };


  const [isLectureFormOpen, setIsLectureFormOpen] = useState(false);
  const [editingLecture, setEditingLecture] = useState(null);
   

    return !selectedEventId ? (
        <MDBCard className="text-center">
            <MDBCardBody className="py-5">
                <MDBIcon fas icon="calendar-alt" size="3x" className="mb-3 text-primary" />
                <h4 className="mb-3">{t("Please Select an Event")}</h4>
                <p className="text-muted mb-4">{t("Select an event to schedule lectures")}</p>
                <div className="w-50 mx-auto">
                    <MDBSelect 
                        label={t("Event")}
                        onChange={event => handleEventSelection(event?.value)}
                        data={[
                            { text: t("Select an event..."), value: "" },
                            ...events.map(event => ({ 
                                text: `${getLocalizedText(event.name)}: ${event.startDate && isoToGermanDate(event.startDate)} - ${event.endDate && isoToGermanDate(event.endDate)}`, 
                                value: event.id 
                            }))
                        ]}
                        value={selectedEventId || ""}
                    />
                </div>
            </MDBCardBody>
        </MDBCard>
    ) : (
        <>
        <MDBCard className="w-auto">
            <MDBCardBody className="flex-column">
                    <MDBRow className="mb-2">
                      <MDBCol size="12" md="6" xl="3">
                        <MDBSelect 
                            label={t("Event")}
                            onChange={event => handleEventSelection(event?.value)}
                            value={selectedEventId}
                            data={[
                                { text: t("Select an event..."), value: "" },
                                ...events.map(event => ({ 
                                    text: getLocalizedText(event.name), 
                                    value: event.id 
                                }))
                            ]}
                        />
                        </MDBCol>
                        <MDBCol md="2">
                          <MDBBtn 
                            color="primary"
                            outline
                            size="sm"
                            onClick={() => {
                              setEditingLecture(null);
                              setIsLectureFormOpen(true);
                            }}
                          >
                            <MDBIcon fas icon="plus" className="me-2" /> 
                            {t("Create Lecture")}
                          </MDBBtn>
                        </MDBCol>

                      </MDBRow>
                    <MDBRow>
                        <MDBCol md="6">
                        <MDBInput type="text" onChange={e => searchLectures(e.target.value)} label={t("Search")} value={searchTerm} />
                        </MDBCol>
                        <MDBCol size="6" md="3">
                        <MDBDatepicker
                            label={t("Start Date")}
                            inline
                            disableFuture
                            defaultValue={filters.createdAtStart && new Date(filters.createdAtStart).toLocaleDateString('en-GB')}
                            value={filters.createdAtStart && new Date(filters.createdAtStart).toLocaleDateString('en-GB')}
                            onChange={date => date && setFilters({ ...filters, createdAtStart: new Date(date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1')).toISOString()})}
                          />
                        </MDBCol>
                        <MDBCol size="6" md="3">
                        <MDBDatepicker
                            label={t("End Date")}
                            inline
                            disableFuture
                            defaultValue={filters.createdAtEnd && new Date(filters.createdAtEnd).toLocaleDateString('en-GB')}
                            value={filters.createdAtEnd && new Date(filters.createdAtEnd).toLocaleDateString('en-GB')}
                            onChange={date => date && setFilters({ ...filters, createdAtEnd: new Date(date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1')).toISOString()})}
                          />
                        </MDBCol>
                    </MDBRow>
                    
                    
                    <MDBRow className="mt-2 mt-xl-3">
                        <MDBCol md="3">
                            <MDBSelect label={t("Type")}
                                onChange={type => type && setFilters({ ...filters, type: type.value})}
                                value={filters.type}
                                data={
                                    [
                                        { text: t("All"), value: null },
                                        { text: t("Marketing"), value: "marketing" },
                                        { text: t("Technical"), value: "technical" },
                                        { text: t("Educational"), value: "educational" },
                                        { text: t("Recruiting"), value: "recruiting" },
                                        { text: t("VIP"), value: "vip" },
                                        { text: t("Promotion Marketing"), value: "promotion_marketing" },
                                        { text: t("Promotion Technical"), value: "promotion_technical" }
                                    ]
                                }
                            />
                        </MDBCol>
                        <MDBCol md="3">
                            <MDBSelect label={t("Status")}
                                onChange={selected => selected && setFilters({ ...filters, status: selected.value})}
                                value={filters.status}
                                data={
                                    [   
                                        { text: t("All"), value: null },
                                        { text: t("Open"), value: "created" },
                                        { text: t("Approved"), value: "checked" },
                                        { text: t("Not Approved"), value: "not_approved" },
                                        { text: t("In Planning"), value: "in_planning" },
                                        { text: t("Committed"), value: "committed" },
                                    ]
                                }
                            />
                        </MDBCol>
                        
                        <MDBCol md="1">
                            <MDBCheckbox label="ECV" onChange={e => setSelectedConditions(e.target.checked ? [...selectedConditions, "ECV"] : selectedConditions.filter(c => c !== "ECV"))} />
                        </MDBCol>
                        <MDBCol md="1">
                            <MDBCheckbox label="Podcast" onChange={e => setSelectedConditions(e.target.checked ? [...selectedConditions, "Podcast"] : selectedConditions.filter(c => c !== "Podcast"))} />
                        </MDBCol>
                        <MDBCol md="1">
                        <MDBIcon className="float-end" fas icon="sync-alt" onClick={() =>  getLectures()} />
                        </MDBCol>
                        <MDBCol md="3">
                              <MDBCheckbox label={t("Filter in Calendar")} onChange={e => setUseFilterInCalendar(e.target.checked)} />
                        </MDBCol>

                        
                    </MDBRow>
                    <div className="d-flex flex-wrap mt-3">
                    <MDBChip key="all" className="me-2 mb-2" style={{backgroundColor: filterTopics.length === 0 ? '#333' : '#6c757d', color: 'white'}} onClick={() => setFilterTopics([])} >
                      {t("All")}
                      </MDBChip>
                    {topics.map((topic) => {
                          const isSelected = filterTopics.includes(topic.id);
                          return (
                          <MDBChip
                            key={topic.id}
                            className="me-2 mb-2"
                            style={{
                              backgroundColor: topic.color || topic.topicGroup?.color || '#ececec',
                              color: isSelected ? 'white' : 'black',
                              border: isSelected ? '2px solid #333' : 'none'
                            }}
                            onClick={() => {
                              if (isSelected) {
                                // Deselect topic
                                setFilterTopics(filterTopics.filter(id => id !== topic.id));
                              } else {
                                // Add topic to selection
                                setFilterTopics([...filterTopics, topic.id]);
                              }
                            }}
                          >
                          {topic.name.find((name) => name.language === i18n.language)?.text || topic.name[0]?.text}
                          </MDBChip>
                        );
                      })}

                    </div>
            </MDBCardBody>
        </MDBCard>
        <MDBRow className="mt-4">
            <MDBCol size="3" style={{maxHeight: "95vh", overflowY: "scroll", overflowX: "hidden"}}>
            <div className="mbsc-col-sm-3 external-drop-cont" ref={setDropElm}>
            <Dropcontainer onItemDrop={onItemDrop} element={dropCont}>
                { isLoading ? <div className="w-100 mx-auto text-center"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>
                  : shownLectures.length > 0 ?
                      shownLectures.map((lecture, index) => {
                          return (
                            <Task
                            key={lecture.id}
                            lecture={lecture} />
                            
                          )
                      })
                      :
                      <tr>
                          <td colSpan="7">
                              {t("No lectures found")}
                          </td>
                      </tr>
                  }
            </Dropcontainer>
            </div>
            </MDBCol>
            <MDBCol size="9" style={{maxHeight: "95vh", overflowY: "scroll", overflowX: "hidden"}}>

            {eventDays.map((day, index) => {
                const { start: dayStart, end: dayEnd } = getEventDayHours(event, day, 'exhibitor');
                return (
                <div key={index} style={{height: "1250px", overflowX: "scroll", overflowY: "hidden"}}>
                <Eventcalendar
                    style={{width: "1200px"}}

                    theme="ios"
                    themeVariant="light"
                    data={useFilterInCalendar ? lecturesCalendar.filter(lecture => lecture.startTime && lecture.endTime && moment(lecture.startTime).format("YYYY-MM-DD") === day).map(lecture => {
                        return {
                            id: lecture.id,
                            title: lecture.title.find((title) => title.language === i18n.language)?.text,
                            start: new Date(lecture.startTime),
                            end: new Date(lecture.endTime),
                            resource: lecture.room,
                            color: getTopicColor(lecture.adminTopic?.id),
                            lecture
                        }
                    } )

                    : lectures.filter(lecture => lecture.startTime && lecture.endTime && moment(lecture.startTime).format("YYYY-MM-DD") === day).map(lecture => {
                        return {
                            id: lecture.id,
                            title: lecture.title.find((title) => title.language === i18n.language)?.text,
                            start: new Date(lecture.startTime),
                            end: new Date(lecture.endTime),
                            resource: lecture.room,
                            color: getTopicColor(lecture.adminTopic?.id),
                            lecture
                        }
                    } )
                    }
                    clickToCreate={true}
                    dragToCreate={true}
                    dragToMove={true}
                    dragToResize={true}
                    eventDelete={true}
                    externalDrop={true}
                    externalDrag={true}
                    onEventClick={(args) => {
                      if (args.event.lecture) {
                        handleLectureClick(args.event.lecture);
                      }
                    }}
                    onEventCreate={onEventCreate}
                    onEventDelete={onEventDelete}
                    onEventUpdate={onEventUpdate}
                    locale={localeDe}
                    invalid={[{
                        start: '00:00',
                        end: dayStart,
                        recurring: {
                            repeat: 'daily'
                        }
                    }, {
                        start: dayEnd,
                        end: '23:59',
                        recurring: {
                            repeat: 'daily'
                        }
                    }]}
                    view={{
                        schedule: {
                            type: 'day',
                            timeCellStep: 60,
                            timeLabelStep: 30,
                            startTime: dayStart,
                            endTime: dayEnd
                        }
                    }}
                    resources={rooms}
                    renderScheduleEvent={customScheduleEvent}
                    groupBy="date"
                    selectedDate={day}
                />
                </div>
                );
            })}
       
            </MDBCol>
        </MDBRow>
        <LectureModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          lecture={selectedLecture}
          // on delete received the lecture it, the lecture is already deleted in the backend
          onDelete={
            (lectureId) => {
              setLectures(oldLectures => oldLectures.filter(lecture => lecture.id !== lectureId));
              setShownLectures(oldShownLectures => oldShownLectures.filter(lecture => lecture.id !== lectureId));
            }
          }
          i18n={i18n}
        />

<MDBModal
      open={isLectureFormOpen}
      tabIndex="-1"
      backdrop={true}
      size="lg"
    >
      <MDBModalDialog size="lg">
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>
              {editingLecture ? t("Edit Lecture") : t("Create Lecture")}
            </MDBModalTitle>
            <MDBBtn 
              className='btn-close' 
              color='none' 
              onClick={() => {
                setIsLectureFormOpen(false);
                setEditingLecture(null);
              }}
            ></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
            <LectureForm
              lectureId={editingLecture?.id}
              eventId={selectedEventId}
              onSave={(savedLecture) => {
                // Add the new lecture to both lectures and shownLectures arrays
                const newLecture = {
                  ...savedLecture,
                  startTime: null,
                  endTime: null,
                  room: null
                };
                console.log("newLecture", newLecture);
                if (editingLecture) {
                  // Update existing lecture
                  setLectures(prev => prev.map(l => l.id === savedLecture.id ? newLecture : l));
                  setShownLectures(prev => prev.map(l => l.id === savedLecture.id ? newLecture : l));
                } else {
                  // Add new lecture
                  setLectures(prev => [...prev, newLecture]);
                  setShownLectures(prev => [...prev, newLecture]);
                }

                setIsLectureFormOpen(false);
                setEditingLecture(null);
              }}
              onCancel={() => {
                setIsLectureFormOpen(false);
                setEditingLecture(null);
              }}
            />
          </MDBModalBody>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
        </>
    ); 
}
